import { convertRawToToken } from "@solomon-apps/shared";
import { BigDecimal } from "@solomon-labs/bigdecimal";
import { WELL_KNOWN_TOKENS_STRINGS } from "@solomon-labs/constants";
import type { JupiterHoldingsDetailedResponse } from "@solomon-labs/swap-client";

const SOL_DECIMALS = 9;

/**
 * Native SOL lamports from a holdings payload, or `null` when the payload
 * carries no native balance at all (i.e. unknown, not zero).
 *
 * Jupiter's Ultra holdings response reports native SOL at the TOP LEVEL
 * (`amount` / `uiAmount`); `native` is an optional convenience mirror the
 * swap client copies from those same fields. Reading only the mirror makes
 * SOL the one asset that silently resolves to "no balance" whenever the
 * mirror is missing, while every SPL token still resolves from `tokens`.
 */
export function getNativeSolRawAmount(holdings: JupiterHoldingsDetailedResponse | undefined): string | null {
    const raw = holdings?.native?.amount ?? holdings?.amount;
    return typeof raw === "string" && /^\d+$/.test(raw) ? raw : null;
}

/**
 * Native SOL as a UI amount, or `null` when unknown. Display-only companion to
 * {@link getNativeSolRawAmount} — use that one for anything that feeds token
 * math, since it keeps the exact lamport value.
 */
export function getNativeSolUiAmount(holdings: JupiterHoldingsDetailedResponse | undefined): number | null {
    const uiAmount = holdings?.native?.uiAmount ?? holdings?.uiAmount;
    return typeof uiAmount === "number" && Number.isFinite(uiAmount) ? uiAmount : null;
}

export function getExactHoldingAmount(
    tokenAddress: string,
    holdings: JupiterHoldingsDetailedResponse | undefined,
    fallbackDecimals?: number
): string | null {
    if (!holdings) return null;

    try {
        if (tokenAddress === WELL_KNOWN_TOKENS_STRINGS.SOL) {
            const rawLamports = getNativeSolRawAmount(holdings);
            return rawLamports === null ? null : convertRawToToken(new BigDecimal(rawLamports), SOL_DECIMALS);
        }

        const accounts = holdings.tokens?.[tokenAddress];
        if (!accounts || accounts.length === 0) return null;

        let decimals = fallbackDecimals;
        let totalRaw = new BigDecimal(0);
        for (const account of accounts) {
            decimals ??= account.decimals;
            if (account.decimals !== decimals) return null;
            totalRaw = totalRaw.add(new BigDecimal(account.amount));
        }

        return decimals === undefined ? null : convertRawToToken(totalRaw, decimals);
    } catch {
        return null;
    }
}
